import { useHistory } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Box } from '@givebutter/primitives/box';
import { BoxIcon } from '@givebutter/primitives/box-icon';
import { Button } from '@givebutter/primitives/button';
import { Separator } from '@givebutter/primitives/separator';
import { useToast } from '@givebutter/primitives/toast';
import { faChevronLeft, faInfoCircle, faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { faBolt } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { publishWorkflowVersion, updateWorkflowStatus } from 'api/workflows';
import * as BuilderFrame from 'assets/js/pages/engage/workflows/components/BuilderFrame';
import { ConflictingTriggersAlert } from 'assets/js/pages/engage/workflows/components/ConflictingTriggersPanel';
import { EditWorkflowReviewActionRows } from 'assets/js/pages/engage/workflows/components/EditWorkflowReviewActionRows';
import { EditWorkflowReviewTriggerRow } from 'assets/js/pages/engage/workflows/components/EditWorkflowReviewTriggerRow';
import { TriggerExecuteOnceSetting } from 'assets/js/pages/engage/workflows/components/TriggerExecuteOnceSetting';
import { WorkflowStatusBadge } from 'assets/js/pages/engage/workflows/components/WorkflowStatusBadge';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentWorkflow } from 'customHooks/useCurrentWorkflow';
import { queryClient } from 'queries/queryClient';
import { workflowQueryKeys } from 'queries/workflow-query-keys';
import { GivebutterAxiosError } from 'types/error';

const EditWorkflowReview = () => {
    const { error, success } = useToast();
    const history = useHistory();
    const { accountId } = useCurrentAccount();
    const { data: workflow } = useCurrentWorkflow();
    const detailPath = `/accounts/${accountId}/workflows/${workflow.id}`;
    const { has_conflicting_triggers: hasConflictingTriggers, has_draft_version: hasDraftVersion } =
        workflow;

    const { mutate: publish, isPending } = useMutation<unknown, GivebutterAxiosError>({
        mutationFn: () =>
            workflow.status === 'active'
                ? publishWorkflowVersion(accountId, workflow.id)
                : updateWorkflowStatus(accountId, workflow.id, { status: 'active' }),
        onSuccess: () => {
            success('Your changes are published!');
            queryClient.removeQueries({
                queryKey: workflowQueryKeys.workflows(accountId),
            });
            history.push(detailPath);
        },
        onError: e => {
            error(e.response?.data.message || 'Unable to publish workflow');
        },
    });

    return (
        <BuilderFrame.Root>
            <BuilderFrame.Title>Reviewing workflow: {workflow.name}</BuilderFrame.Title>
            <BuilderFrame.Header>
                <div className="flex items-center gap-8">
                    <Button
                        onClick={() => history.goBack()}
                        variant="transparent"
                        size="sm"
                        shape="square"
                        color="neutral"
                        aria-label="Go back"
                    >
                        <FontAwesomeIcon icon={faChevronLeft} size="lg" />
                    </Button>

                    <div className="flex items-center gap-2">
                        {workflow.status == 'active' && (
                            <WorkflowStatusBadge status={workflow.status} />
                        )}
                        <div className="flex items-center gap-2">
                            {workflow.icon && <span>{workflow.icon}</span>}
                            <span className="font-medium">{workflow.name}</span>
                        </div>
                    </div>
                </div>
                <div className="flex w-full items-center justify-center gap-2 md:w-auto">
                    <Button
                        variant="outline"
                        color="neutral"
                        onClick={() => history.push(detailPath)}
                    >
                        Finish later
                    </Button>
                    <AlertDialog.Root type="warning">
                        <AlertDialog.Trigger asChild>
                            <Button
                                disabled={isPending}
                                variant="solid"
                                color="primary"
                                onClick={e => {
                                    if (!hasDraftVersion) {
                                        e.preventDefault();
                                        publish();
                                    }
                                }}
                            >
                                {isPending ? (
                                    <FontAwesomeIcon
                                        icon={faSpinnerThird}
                                        className="animate-spin"
                                    />
                                ) : (
                                    <FontAwesomeIcon icon={faBolt} />
                                )}
                                Publish changes
                            </Button>
                        </AlertDialog.Trigger>
                        <AlertDialog.Portal>
                            <AlertDialog.Overlay />
                            <AlertDialog.Content>
                                <AlertDialog.Header>
                                    <AlertDialog.Icon />
                                    <AlertDialog.Close />
                                </AlertDialog.Header>
                                <AlertDialog.Body>
                                    <AlertDialog.Title>Publish new changes?</AlertDialog.Title>
                                    <AlertDialog.Description>
                                        These new changes will only apply to new contacts entering
                                        the workflow, and will not affect existing contacts already
                                        in the workflow.
                                    </AlertDialog.Description>
                                </AlertDialog.Body>
                                <AlertDialog.Footer>
                                    <AlertDialog.Cancel onClick={e => e.stopPropagation()}>
                                        Close
                                    </AlertDialog.Cancel>
                                    <AlertDialog.Action
                                        onClick={() => publish()}
                                        disabled={isPending}
                                    >
                                        Publish
                                    </AlertDialog.Action>
                                </AlertDialog.Footer>
                            </AlertDialog.Content>
                        </AlertDialog.Portal>
                    </AlertDialog.Root>
                </div>
            </BuilderFrame.Header>
            <BuilderFrame.Content>
                <div className="bg-gray-25 flex h-full w-full flex-col items-center justify-start gap-10 overflow-y-auto p-6 py-14 shadow-inner">
                    {hasDraftVersion && (
                        <Box className="flex w-full max-w-3xl flex-shrink-0 items-center gap-4 p-4">
                            <BoxIcon size="md">
                                <FontAwesomeIcon icon={faInfoCircle} />
                            </BoxIcon>
                            <div className="flex flex-col">
                                <p className="text-sm font-medium">
                                    Changes will only apply to future contacts
                                </p>
                                <p className="text-sm">
                                    These new changes will not affect existing contacts in the
                                    workflow.
                                </p>
                            </div>
                        </Box>
                    )}
                    <div className="flex flex-col gap-2">
                        <h2 className="text-center text-3xl font-medium">
                            {hasDraftVersion
                                ? 'Review your new changes'
                                : 'Your workflow is almost finished!'}
                        </h2>
                        <p className="max-w-xl text-center">
                            Review the details below to ensure the workflow frequency, trigger, and
                            actions are accurate before{' '}
                            {hasDraftVersion
                                ? 'publishing changes to your active workflow.'
                                : 'turning on your workflow.'}
                        </p>
                    </div>
                    <div className="flex w-full max-w-3xl flex-col gap-4">
                        {hasConflictingTriggers && (
                            <Box className="p-4">
                                <ConflictingTriggersAlert className="max-w-none" />
                            </Box>
                        )}
                        <Box>
                            <p className="px-6 py-4 text-xl font-medium">Frequency</p>
                            <Separator />
                            <TriggerExecuteOnceSetting />
                        </Box>
                        <Box>
                            <p className="px-6 py-4 text-xl font-medium">Trigger</p>
                            <Separator />
                            <EditWorkflowReviewTriggerRow />
                        </Box>
                        <Box>
                            <p className="px-6 py-4 text-xl font-medium">Actions</p>
                            <Separator />
                            <EditWorkflowReviewActionRows />
                        </Box>
                    </div>
                </div>
            </BuilderFrame.Content>
        </BuilderFrame.Root>
    );
};

export { EditWorkflowReview };
